import React, { useEffect, useState } from "react";
import { PageHeader, Card, Badge, Button, Spinner, Input, Select } from "@/components/ui";
import { endpoints } from "@/lib/api";
import { fmt, stageColors, stageLabels } from "@/lib/format";
import { ArrowRight, Plus, Briefcase, Users } from "lucide-react";
import { InvestorMatchPanel } from "@/components/InvestorMatch";

const STAGES = ["sourcing", "diligence", "negotiation", "closing", "closed", "exited"];

export default function Deals() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [matchDeal, setMatchDeal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await endpoints.deals();
      setDeals(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const advance = async (d) => {
    const idx = STAGES.indexOf(d.stage);
    if (idx < 0 || idx >= STAGES.length - 1) return;
    setUpdating(d.id);
    await endpoints.updateDeal(d.id, { stage: STAGES[idx + 1] });
    await load();
    setUpdating(null);
  };

  const byStage = STAGES.reduce((acc, s) => {
    acc[s] = deals.filter((d) => d.stage === s);
    return acc;
  }, {});

  return (
    <div className="p-4 md:p-10 max-w-[1600px] mx-auto">
      <PageHeader
        eyebrow="CLARA Core · M&A"
        title="Deal Pipeline"
        description="Kanban view of buy-side, sell-side and merger transactions across the deal lifecycle."
        testid="deals-page"
      />

      {loading ? (
        <div className="text-white/50"><Spinner /> Loading…</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {STAGES.map((s) => {
            const items = byStage[s];
            const total = items.reduce((sum, d) => sum + (d.value || 0), 0);
            const c = stageColors[s];
            return (
              <div key={s} className="clara-card p-3" data-testid={`stage-column-${s}`}>
                <div className="flex items-center justify-between mb-3 pb-2" style={{ borderBottom: `1px solid ${c.border}40` }}>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: c.text }}>
                      {stageLabels[s]}
                    </div>
                    <div className="text-[11px] text-white/40 mt-0.5 font-mono">{items.length} · {fmt.money(total)}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {items.map((d) => (
                    <div key={d.id} className="clara-surface-2 p-3 rounded-md border border-white/5 hover:border-[#C5A059]/30 transition" data-testid={`deal-card-${d.id}`}>
                      <div className="text-[13px] font-medium leading-tight mb-2 line-clamp-2">{d.company_name}</div>
                      <div className="flex items-center justify-between mb-2 text-[11px] text-white/50">
                        <span className="font-mono">{fmt.money(d.value)}</span>
                        <span>{d.probability}%</span>
                      </div>
                      <div className="text-[10px] text-white/30 mb-2">{d.lead || "—"}</div>
                      {s !== "exited" && s !== "closed" && (
                        <button onClick={() => setMatchDeal(d)} data-testid={`investors-${d.id}`}
                          className="w-full mb-1.5 text-[10px] py-1 rounded border border-[#C5A059]/25 text-gold/80 hover:text-gold hover:border-[#C5A059]/60 transition flex items-center justify-center gap-1">
                          <Users size={10} /> Investor matches
                        </button>
                      )}
                      {s !== "exited" && s !== "closed" && (
                        <button
                          onClick={() => advance(d)}
                          disabled={updating === d.id}
                          data-testid={`advance-${d.id}`}
                          className="w-full text-[10px] py-1 rounded border border-white/10 hover:border-[#C5A059]/50 hover:text-[#C5A059] transition flex items-center justify-center gap-1"
                        >
                          {updating === d.id ? <Spinner size={10} /> : <>Advance <ArrowRight size={10} /></>}
                        </button>
                      )}
                    </div>
                  ))}
                  {!items.length && (
                    <div className="text-center text-[11px] text-white/20 py-6 border border-dashed border-white/5 rounded-md">
                      Empty
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {matchDeal && <InvestorMatchPanel deal={matchDeal} onClose={() => setMatchDeal(null)} />}
    </div>
  );
}
